<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import moment from 'moment-jalaali'
import { useField, useForm } from 'vee-validate'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as yup from 'yup'
import {
  type CreateInternationalAccountPayload,
  type CreateInternationalWiseAccountPayload,
  createInternationalAccountAction,
  createInternationalPaypalAccountAction,
  createInternationalRevolutAccountAction,
  createInternationalWiseAccountAction,
  createIranianAccountAction,
  createIranianSrcAccountAction,
} from '@/api/functions/bank-account/create-bank-account.action'
import { getUserDetailAction } from '@/api/functions/users/get-user-detail.action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import ErrorIcon from '/svg/error-icon.svg'

const router = useRouter()
const toast = useToast()
const route = useRoute()

// Reactive data
const userId = route.params.id as string
const accountType = ref('')
const internationalType = ref('')
const loading = ref(false)

const { data: userDetail } = useQueryApi({
  queryKey: ['user-detail', userId],
  queryFn: () => getUserDetailAction(userId),
  enabled: !!userId,
})

const userData = computed(() => ({
  detail: userDetail.value?.data,
}))

// Options
const accountTypeOptions = [
  { label: 'حساب مقصد در خارج از کشور', value: 'بین المللی' },
  { label: 'حساب مقصد ریالی', value: 'مقصد ریالی' },
  { label: 'حساب مبداء ریالی', value: 'مبداء ریالی' },
]

const internationalTypeOptions = [
  { label: 'بین بانکی', value: 'بین بانکی' },
  { label: 'رولوت', value: 'رولوت' },
  { label: 'پی پال', value: 'پی پال' },
  { label: 'وایز', value: 'وایز' },
]

const poundOptions = [
  { label: 'بله', value: true },
  { label: 'خیر', value: false },
]

const dayOptions = computed(() =>
  Array.from({ length: 31 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString().padStart(2, '0'),
  }))
)

const monthOptions = computed(() =>
  Array.from({ length: 12 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString().padStart(2, '0'),
  }))
)

const yearOptions = computed(() =>
  Array.from({ length: 104 }, (_, i) => ({
    label: (1403 - i).toString(),
    value: (1403 - i).toString(),
  }))
)

// Validation Schema
const createValidationSchema = () => {
  const baseInternationalSchema = {
    bankName: yup
      .string()
      .required('نام بانک الزامی است')
      .min(2, 'نام بانک باید حداقل ۲ کاراکتر باشد')
      .max(100, 'نام بانک باید حداکثر ۱۰۰ کاراکتر باشد')
      .matches(/^[a-zA-Z ]*$/, 'نام بانک باید فقط حروف انگلیسی باشد'),

    ownerName: yup
      .string()
      .required('نام دارنده حساب الزامی است')
      .min(3, 'نام دارنده حساب باید حداقل ۳ کاراکتر باشد')
      .max(100, 'نام دارنده حساب باید حداکثر ۱۰۰ کاراکتر باشد')
      .matches(/^[a-zA-Z ]*$/, 'نام دارنده حساب باید فقط حروف انگلیسی باشد'),

    country: yup.string().required('کشور الزامی است'),
  }

  const baseDomesticSchema = {
    sheba: yup
      .string()
      .required('شماره شبا الزامی است')
      .matches(/^[0-9]{24}$/, 'شماره شبا باید ۲۴ رقم باشد'),

    cardNumber: yup
      .string()
      .required('شماره کارت الزامی است')
      .length(16, 'شماره کارت باید ۱۶ رقمی باشد')
      .matches(/^[0-9]+$/, 'شماره کارت باید فقط عدد باشد'),

    bankName: yup
      .string()
      .required('نام بانک الزامی است')
      .min(2, 'نام بانک باید حداقل ۲ کاراکتر باشد')
      .max(100, 'نام بانک باید حداکثر ۱۰۰ کاراکتر باشد'),

    ownerName: yup
      .string()
      .required('نام دارنده حساب الزامی است')
      .min(3, 'نام دارنده حساب باید حداقل ۳ کاراکتر باشد')
      .max(100, 'نام دارنده حساب باید حداکثر ۱۰۰ کاراکتر باشد'),

    nationalCode: yup
      .string()
      .required('کد ملی الزامی است')
      .length(10, 'کد ملی باید ۱۰ رقمی باشد')
      .matches(/^[0-9]+$/, 'کد ملی باید فقط عدد باشد'),
  }

  if (accountType.value === 'بین المللی') {
    if (internationalType.value === 'بین بانکی' || internationalType.value === 'وایز') {
      return yup.object({
        ...baseInternationalSchema,
        iban: yup
          .string()
          .required('شماره IBAN الزامی است')
          .transform((value) => value.replace(/\s|-/g, '').toUpperCase())
          .matches(/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/, 'فرمت IBAN معتبر نیست')
          .min(15, 'شماره IBAN باید حداقل ۱۵ کاراکتر باشد')
          .max(34, 'شماره IBAN باید حداکثر ۳۴ کاراکتر باشد'),

        bic: yup
          .string()
          .required('شماره BIC الزامی است')
          .min(8, 'شماره BIC باید حداقل ۸ کاراکتر باشد')
          .max(11, 'شماره BIC باید حداکثر ۱۱ کاراکتر باشد'),
        isPound: yup.boolean().nullable(),
        sortCode: yup.string().when('isPound', {
          is: true,
          then: (schema) => schema.required('شماره Sort Code برای حساب پوند الزامی است'),
          otherwise: (schema) => schema.notRequired(),
        }),
        accountNumber: yup.string().when('isPound', {
          is: true,
          then: (schema) => schema.required('شماره Account Number برای حساب پوند الزامی است'),
          otherwise: (schema) => schema.notRequired(),
        }),
      })
    } else if (internationalType.value === 'رولوت') {
      return yup.object({
        ...baseInternationalSchema,
        iban: yup
          .string()
          .required('شماره IBAN الزامی است')
          .transform((value) => value.replace(/\s|-/g, '').toUpperCase())
          .matches(/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/, 'فرمت IBAN معتبر نیست')
          .min(15, 'شماره IBAN باید حداقل ۱۵ کاراکتر باشد')
          .max(34, 'شماره IBAN باید حداکثر ۳۴ کاراکتر باشد'),

        bic: yup
          .string()
          .required('شماره BIC الزامی است')
          .min(8, 'شماره BIC باید حداقل ۸ کاراکتر باشد')
          .max(11, 'شماره BIC باید حداکثر ۱۱ کاراکتر باشد'),
        revolutTag: yup
          .string()
          .required('Revolut Tag الزامی است')
          .min(2, 'Revolut Tag باید حداقل ۲ کاراکتر باشد')
          .max(50, 'Revolut Tag باید حداکثر ۵۰ کاراکتر باشد'),
      })
    } else if (internationalType.value === 'پی پال') {
      return yup.object({
        ...baseInternationalSchema,
        paypalEmail: yup.string().required('ایمیل Paypal الزامی است').email('فرمت ایمیل معتبر نیست'),
        paypalId: yup
          .string()
          .required('ایمیل Paypal الزامی است')
          .nullable()
          .max(50, 'Paypal ID باید حداکثر ۵۰ کاراکتر باشد'),
      })
    }
  } else if (accountType.value === 'مقصد ریالی' || accountType.value === 'مبداء ریالی') {
    const domesticSchema = { ...baseDomesticSchema }

    return yup.object({
      ...domesticSchema,
      birthDate: yup.object({
        day: yup.string().required('روز تولد الزامی است'),
        month: yup.string().required('ماه تولد الزامی است'),
        year: yup.string().required('سال تولد الزامی است'),
      }),
    })
  }

  return yup.object({})
}
// Reactive validation schema
const validationSchema = computed(() => createValidationSchema())

// Form setup
const { handleSubmit, resetForm, errors } = useForm({
  validationSchema,
  initialValues: {
    isPound: null,
    country: '',
    birthDate: {
      day: '',
      month: '',
      year: '',
    },
    paypalEmail: '',
    paypalId: '',
    iban: '',
    bic: '',
    bankName: '',
    ownerName: '',
    sortCode: '',
    accountNumber: '',
    revolutTag: '',
    sheba: '',
    cardNumber: '',
    nationalCode: '',
  },
})
// Fields - International
const { value: paypalEmail } = useField('paypalEmail')
const { value: paypalId } = useField('paypalId')
const { value: iban } = useField('iban')
const { value: bic } = useField('bic')
const { value: bankName } = useField('bankName')
const { value: ownerName } = useField('ownerName')
const { value: country } = useField('country')
const { value: isPound } = useField('isPound')
const { value: sortCode } = useField('sortCode')
const { value: accountNumber } = useField('accountNumber')
const { value: revolutTag } = useField('revolutTag')

// Fields - Domestic
const { value: sheba } = useField('sheba')
const { value: cardNumber } = useField('cardNumber')
const { value: nationalCode } = useField('nationalCode')
const { value: birthDate } = useField<{ day: string; month: string; year: string }>('birthDate')

// Computed fields for dynamic labels
const ibanField = computed({
  get: () => (internationalType.value === 'پی پال' ? paypalEmail.value : iban.value),
  set: (value) => {
    if (internationalType.value === 'پی پال') {
      paypalEmail.value = value
    } else {
      iban.value = value
    }
  },
})

const bicField = computed({
  get: () => (internationalType.value === 'پی پال' ? paypalId.value : bic.value),
  set: (value) => {
    if (internationalType.value === 'پی پال') {
      paypalId.value = value
    } else {
      bic.value = value
    }
  },
})

// Methods
const onAccountTypeChange = () => {
  internationalType.value = ''
  resetForm()
}

const onInternationalTypeChange = () => {
  resetForm()
}

// Mutation for international accounts
const { mutate: createInternationalAccount } = useMutationApi({
  mutationKey: ['createInternationalAccount'],
  mutationFn: createInternationalAccountAction,
  onSuccess: () => {
    loading.value = false
    toast.success('حساب بین‌المللی با موفقیت ایجاد شد', 'موفقیت')
    router.push(`/users/${userId}/accounts`)
  },
  onError: (error: Error) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Mutation for international revolut accounts
const { mutate: createInternationalRevolutAccount } = useMutationApi({
  mutationKey: ['createInternationalRevolutAccount'],
  mutationFn: createInternationalRevolutAccountAction,
  onSuccess: () => {
    loading.value = false
    toast.success('حساب رولوت با موفقیت ایجاد شد', 'موفقیت')
    router.push(`/users/${userId}/accounts`)
  },
  onError: (error: Error) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})
// Mutation for international Wise accounts
const { mutate: createInternationalWiseAccount } = useMutationApi({
  mutationKey: ['createInternationalWiseAccount'],
  mutationFn: createInternationalWiseAccountAction,
  onSuccess: () => {
    loading.value = false
    toast.success('حساب وایز با موفقیت ایجاد شد', 'موفقیت')
    router.push(`/users/${userId}/accounts`)
  },
  onError: (error: Error) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})
// Mutation for international paypal accounts
const { mutate: createInternationalPaypalAccount } = useMutationApi({
  mutationKey: ['createInternationalPaypalAccount'],
  mutationFn: createInternationalPaypalAccountAction,
  onSuccess: () => {
    loading.value = false
    toast.success('حساب پی پال با موفقیت ایجاد شد', 'موفقیت')
    router.push(`/users/${userId}/accounts`)
  },
  onError: (error: Error) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Mutation for iranian accounts
const { mutate: createIranianAccount } = useMutationApi({
  mutationKey: ['createIranianAccount'],
  mutationFn: createIranianAccountAction,
  onSuccess: () => {
    loading.value = false
    toast.success('حساب مقصد ریالی با موفقیت ایجاد شد', 'موفقیت')
    router.push(`/users/${userId}/accounts`)
  },
  onError: (error: Error) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Mutation for iranian source accounts
const { mutate: createIranianSrcAccount } = useMutationApi({
  mutationKey: ['createIranianSrcAccount'],
  mutationFn: createIranianSrcAccountAction,
  onSuccess: () => {
    loading.value = false
    toast.success('حساب مبداء ریالی با موفقیت ایجاد شد', 'موفقیت')
    router.push(`/users/${userId}/accounts`)
  },
  onError: (error: Error) => {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const submitForm = handleSubmit((values: unknown) => {
  loading.value = true

  try {
    if (!userData?.value?.detail?.id) return
    const formValues = values as Record<string, any>
    switch (accountType.value) {
      case 'بین المللی':
        switch (internationalType.value) {
          case 'بین بانکی': {
            const bankTransferPayload: { userId: string } & CreateInternationalAccountPayload = {
              userId: userData.value.detail.id,
              iban_number: formValues.iban,
              bic_number: formValues.bic,
              bank_name: formValues.bankName,
              owner_full_name: formValues.ownerName,
              country_id: formValues.country,
            }
            if (formValues.isPound) {
              bankTransferPayload.sort_code = formValues.sortCode
              bankTransferPayload.account_number = formValues.accountNumber
              bankTransferPayload.is_pound = true
            }
            createInternationalAccount(bankTransferPayload)
            break
          }
          case 'وایز': {
            const wisePayload: { userId: string } & CreateInternationalWiseAccountPayload = {
              userId: userData.value.detail.id,
              iban_number: formValues.iban,
              bic_number: formValues.bic,
              bank_name: formValues.bankName,
              owner_full_name: formValues.ownerName,
              country_id: formValues.country,
            }
            if (formValues.isPound) {
              wisePayload.sort_code = formValues.sortCode
              wisePayload.account_number = formValues.accountNumber
              wisePayload.is_pound = true
            }
            createInternationalWiseAccount(wisePayload)
            break
          }
          case 'رولوت':
            createInternationalRevolutAccount({
              userId: userData.value.detail.id,
              iban_number: formValues.iban,
              bic_number: formValues.bic,
              bank_name: formValues.bankName,
              owner_full_name: formValues.ownerName,
              country_id: formValues.country,
              revolut_tag: formValues.revolutTag,
            })
            break

          case 'پی پال':
            createInternationalPaypalAccount({
              userId: userData.value.detail.id,
              paypal_email: formValues.paypalEmail,
              paypal_id: formValues.paypalId || undefined,
              bank_name: formValues.bankName,
              owner_full_name: formValues.ownerName,
              country_id: formValues.country,
            })
            break
        }
        break

      case 'مقصد ریالی':
      case 'مبداء ریالی': {
        const shebaWithIR = `IR${formValues.sheba}`
        const jalaaliDateString = `${formValues.birthDate.year}/${formValues.birthDate.month}/${formValues.birthDate.day}`
        const gregorianDate = moment(jalaaliDateString, 'jYYYY/jMM/jDD').format('YYYY-MM-DD')

        if (accountType.value === 'مقصد ریالی') {
          createIranianAccount({
            userId: userData?.value?.detail?.id,
            sheba_number: shebaWithIR,
            card_number: formValues.cardNumber,
            bank_name: formValues.bankName,
            owner_full_name: formValues.ownerName,
            national_code: formValues.nationalCode,
            birth_date: gregorianDate,
          })
        } else {
          createIranianSrcAccount({
            userId: userData?.value?.detail?.id,
            sheba_number: shebaWithIR,
            card_number: formValues.cardNumber,
            bank_name: formValues.bankName,
            owner_full_name: formValues.ownerName,
            national_code: formValues.nationalCode,
            birth_date: gregorianDate,
          })
        }
        break
      }
    }
  } catch (error) {
    loading.value = false
    toast.error(getErrorMessage(error), 'خطا')
  }
})

const filterEnglishCharacters = (event: Event) => {
  const input = event.target as HTMLInputElement
  const englishOnlyRegex = /^[a-zA-Z ]*$/
  if (!englishOnlyRegex.test(input.value)) {
    input.value = input.value.replace(/[^a-zA-Z ]/g, '')
  }
}
</script>

<template>
  <div class="mb-28">
    <div class="">
      <!-- Header -->
      <router-link :to="`/users/${userId}/accounts`" class="w-fit mb-3 gap-1 flex items-center text-[#1b325a] text-bae">
        <img src="/svg/back-icon.svg" class="me-1" width="24" />
        <span>بازگشت</span>
        <span>|</span>
        <span>حساب‌ها</span>
      </router-link>

      <!-- Main Content -->
      <div class="bg-white rounded-xl shadow-sm" :class="accountType ? 'p-5' : 'p-5'">
        <!-- Account Type Selection -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-6"
          :class="accountType ? 'lg:h-[60px]' : 'lg:h-[55px]'">
          <div>
            <Select v-model="accountType" @update:modelValue="onAccountTypeChange" label="انتخاب نوع حساب"
              :options="accountTypeOptions" />
          </div>

          <div v-if="accountType === 'بین المللی'">
            <Select v-model="internationalType" @update:modelValue="onInternationalTypeChange"
              label="نوع حساب مقصد خارجی" :options="internationalTypeOptions" />
          </div>
        </div>

        <!-- Info Banner -->
        <div v-if="accountType && (accountType !== 'بین المللی' || internationalType)" class="mb-10">
          <div class="bg-[#faf2db] rounded-xl p-2.5 text-black text-sm font-normal">
            <p v-if="accountType === 'بین المللی'">
              حساب بانکی که خریدار قصد دارد مبلغ ارزی به آن واریز شود برای مثال (حساب بانکی، پی پال،
              رولوت، وایز و ...)
            </p>
            <p v-if="accountType === 'مقصد ریالی'">
              حساب بانکی که فروشنده قصد دارد تسویه ریالی توسط پی جیب، با آن حساب انجام شود.
            </p>
            <p v-if="accountType === 'مبداء ریالی'">
              حساب بانکی که خریدار از طریق این حساب قصد واریز مبلغ ریالی به پی جیب را دارد.
            </p>
          </div>
        </div>

        <!-- Form -->
        <form v-if="accountType && (accountType !== 'بین المللی' || internationalType)" @submit="submitForm"
          class="space-y-6">
          <!-- International Account Fields -->
          <div v-if="accountType === 'بین المللی'">
            <!-- PayPal Fields -->
            <div v-if="internationalType === 'پی پال'" class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
              <div>
                <Input v-model="ibanField" name="ibanField" label="ایمیل Paypal را وارد کنید" type="email"
                  placeholder="example@paypal.com" />
                <div v-if="errors['paypalEmail']"
                  class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors['paypalEmail'] }}</span>
                </div>
              </div>
              <div>
                <Input v-model="bicField" name="bicField" :isTag="true" label="Paypal Id را وارد کنید" type="text"
                  placeholder="PID123456789" />
                <div v-if="errors['paypalId']"
                  class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors['paypalId'] }}</span>
                </div>
              </div>
            </div>

            <!-- IBAN & BIC Fields -->
            <div
              v-if="internationalType === 'بین بانکی' || internationalType === 'رولوت' || internationalType === 'وایز'"
              class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
              <div>
                <Input v-model="ibanField" name="ibanField" label="شماره IBAN را وارد کنید" type="text"
                  placeholder="IE84REVO99036020197146" />
                <div v-if="errors['iban']" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors['iban'] }}</span>
                </div>
              </div>
              <div>
                <Input v-model="bicField" name="bicField" label="شماره BIC CODE را وارد کنید" type="text"
                  placeholder="REVOIE2D" />
                <div v-if="errors['bic']" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors['bic'] }}</span>
                </div>
              </div>
            </div>

            <!-- Common International Fields -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
              <div>
                <Input v-model="bankName" name="bankName" @input="filterEnglishCharacters"
                  label="نام بانک را به انگلیسی وارد کنید" type="text" placeholder="Bank Name" />
                <!-- <div v-if="errors.bankName" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors.bankName }}</span>
                </div> -->
              </div>
              <div>
                <Input v-model="ownerName" name="ownerName" @input="filterEnglishCharacters"
                  label="نام دارنده حساب را به انگلیسی وارد کنید" type="text" placeholder="Full Name" />
                <!-- <div v-if="errors.ownerName" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors.ownerName }}</span>
                </div> -->
              </div>
            </div>

            <div class="flex flex-col lg:grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <CountrySelect v-model="country" name="country" label="کشور مقصد حساب ارزی خود را انتخاب کنید" />
                <div v-if="errors.country" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors.country }}</span>
                </div>
              </div>

              <div v-if="internationalType === 'بین بانکی' || internationalType === 'وایز'" class="pt-3">
                <Radio v-model="isPound" name="isPound" label="آیا حساب شما پوند است؟" :options="poundOptions" />
              </div>

              <!-- Bank Transfer Specific Fields -->
              <div v-if="isPound === true && (internationalType === 'بین بانکی' || internationalType === 'وایز')"
                class="col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <Input v-model="accountNumber" name="accountNumber" label="مقدار Account Number را وارد کنید"
                    type="text" placeholder="12345678" />
                  <div v-if="errors['accountNumber']"
                    class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors['accountNumber'] }}</span>
                  </div>
                </div>
                <div>
                  <Input v-model="sortCode" name="sortCode" label="مقدار Sort Code را وارد کنید" type="text"
                    placeholder="123456" />
                  <div v-if="errors['sortCode']"
                    class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors['sortCode'] }}</span>
                  </div>
                </div>
              </div>

              <!-- Revolut Specific Field -->
              <div v-if="internationalType === 'رولوت'">
                <Input v-model="revolutTag" :isTag="true" name="revolutTag" label="Revolut Tag را وارد کنید" type="text"
                  placeholder="@username" />
                <!-- <div v-if="errors['revolutTag']"
                  class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors['revolutTag'] }}</span>
                </div> -->
              </div>
            </div>

          </div>

          <!-- Domestic Account Fields -->
          <div v-if="accountType === 'مقصد ریالی' || accountType === 'مبداء ریالی'">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
              <div>
                <Input v-model="sheba" name="sheba" :isSheba="true" label="شماره شبا را وارد کنید" type="text"
                  placeholder="060120000000009886055009" />
                <!-- <div v-if="errors.sheba" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors.sheba }}</span>
                </div> -->
              </div>
              <div>
                <Input v-model="cardNumber" name="cardNumber" label="شماره کارت را وارد کنید" type="text"
                  placeholder="6104338650180389" />
                <!-- <div v-if="errors.cardNumber"
                  class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors.cardNumber }}</span>
                </div> -->
              </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
              <div>
                <Input v-model="bankName" name="bankName" label="نام بانک را وارد کنید" type="text"
                  placeholder="ملی تست" />
                <!-- <div v-if="errors.bankName" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors.bankName }}</span>
                </div> -->
              </div>
              <div>
                <Input v-model="ownerName" name="ownerName" label="نام دارنده حساب را وارد کنید" type="text" />
                <!-- <div v-if="errors.ownerName" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors.ownerName }}</span>
                </div> -->
              </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <Input v-model="nationalCode" name="nationalCode" label="کد ملی دارنده حساب را وارد کنید" type="text" />
                <!-- <div v-if="errors.nationalCode"
                  class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>{{ errors.nationalCode }}</span>
                </div> -->
              </div>
              <div>
                <div class="grid lg:grid-cols-3 gap-4">
                  <Select v-model="birthDate.day" name="birthDate.day" :options="dayOptions" label="روز تولد" />
                  <Select v-model="birthDate.month" name="birthDate.month" :options="monthOptions" label="ماه تولد" />
                  <Select v-model="birthDate.year" name="birthDate.year" :options="yearOptions" label="سال تولد" />
                </div>
                <div v-if="errors.birthDate" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                  <img :src="ErrorIcon" width="15" alt="error-icon" />
                  <span>تاریخ تولد الزامی است</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Submit Buttons -->
          <div class="flex flex-col lg:flex-row gap-3 mt-8 mb-2">
            <Button :isAdmin="true" type="submit" :disabled="loading"
              class="px-6 h-12! py-0 text-base rounded-[10px] font-medium">
              <span>{{ loading ? 'در حال ثبت...' : 'ثبت و افزودن حساب بانکی' }}</span>
            </Button>
            <Button type="button" @click="router.push('/profile/bank-accounts')"
              class="px-6 h-12! py-0 hover:bg-gray-100 text-base rounded-[10px] font-medium bg-transparent border-[#2d5396] border-2 !text-[#2d5396]">
              لغو
            </Button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
